<template>
  <div class="assistant-cat-dialog-mask">
    <div class="assistant-cat-dialog-content" @click.stop>
      <!-- ヘッダー部分 -->
      <div class="dialog-header">
        <h2 class="dialog-title">{{ $t("myPage.assistantCatDialog.title") }}</h2>
        <button class="close-button" @click="handleClose" :aria-label="$t('myPage.assistantCatDialog.closeAriaLabel')">
          <span class="close-icon">×</span>
        </button>
      </div>

      <!-- ローディング状態 -->
      <div v-if="isLoading" class="loading-container">
        <LoadingSpinner size="large" variant="overlay" :text="$t('myPage.assistantCatDialog.loadingText')" />
      </div>

      <!-- エラー状態 -->
      <div v-else-if="error" class="error-container">
        <p class="error-message">{{ error }}</p>
        <button class="retry-button" @click="handleRetry">{{ $t("myPage.assistantCatDialog.retryButton") }}</button>
      </div>

      <!-- メインコンテンツ -->
      <div v-else class="dialog-body">
        <!-- 説明文 -->
        <p class="description">
          {{ $t("myPage.assistantCatDialog.descriptionLine1") }}<br />{{
            $t("myPage.assistantCatDialog.descriptionLine2")
          }}
        </p>

        <!-- グループタブ -->
        <div class="group-tabs">
          <button
            v-for="group in groupOptions"
            :key="group.code"
            class="group-tab"
            :class="{
              'group-tab--active': selectedGroup === group.code,
              [`group-tab--${group.code.toLowerCase()}`]: selectedGroup === group.code,
            }"
            @click="handleGroupSelect(group.code)"
          >
            <img
              :src="getGroupIcon(group.code)"
              :alt="group.name + $t('myPage.assistantCatDialog.groupIconSuffix')"
              class="group-icon"
            />
            <span>{{ group.name }}</span>
          </button>
        </div>

        <!-- 猫カードカルーセル -->
        <div class="carousel-container">
          <swiper ref="catSwiper" :options="swiperOptions" class="cat-swiper">
            <swiper-slide v-for="cat in filteredCats" :key="cat.assistantCatType" class="cat-slide">
              <div
                class="cat-card"
                :class="{
                  'cat-card--selected': cat.selected,
                  'cat-card--writer-type': isWriterTypeCat(cat),
                  'cat-card--premium-prompt': shouldShowPremiumPrompt(cat),
                }"
              >
                <!-- グループアイコン（右上） -->
                <div class="cat-group-badge">
                  <img
                    :src="getGroupIcon(cat.group)"
                    :alt="cat.groupName + $t('myPage.assistantCatDialog.groupSuffix')"
                    class="cat-group-icon"
                  />
                </div>

                <!-- 猫アイコン -->
                <div class="cat-icon-container">
                  <CatIcon :writer-type="cat.assistantCatType" :clickable="false" size="large" />
                </div>

                <!-- 猫名前 -->
                <div class="cat-names">
                  <div class="cat-name-japanese">{{ cat.officialNameJapanese }}</div>
                  <div class="cat-name-english">{{ cat.officialName }}</div>
                </div>

                <!-- ステータスラベル -->
                <div class="cat-status">
                  <div
                    v-if="isWriterTypeCat(cat)"
                    class="status-label status-label--writer-type"
                    :class="`status-label--${cat.group.toLowerCase()}`"
                  >
                    {{ $t("myPage.assistantCatDialog.writerTypeLabel") }}
                  </div>
                  <div
                    v-else-if="cat.isSynergyType"
                    class="status-label status-label--synergy"
                    :class="`status-label--${cat.group.toLowerCase()}`"
                  >
                    {{ $t("myPage.assistantCatDialog.synergyTypeLabel") }}
                  </div>
                  <div
                    v-else-if="cat.isImpactType"
                    class="status-label status-label--impact"
                    :class="`status-label--${cat.group.toLowerCase()}`"
                  >
                    {{ $t("myPage.assistantCatDialog.impactTypeLabel") }}
                  </div>
                </div>

                <!-- 説明文 -->
                <p class="cat-description">{{ cat.description }}</p>

                <!-- アクションボタン -->
                <div class="cat-action">
                  <button v-if="cat.selected" class="action-button action-button--disabled" disabled>
                    {{ $t("myPage.assistantCatDialog.selectedButton") }}
                  </button>
                  <button v-else class="action-button action-button--active" @click="handleSelectCat(cat)">
                    {{ $t("myPage.assistantCatDialog.changeButton") }}
                  </button>
                </div>

                <!-- プレミアム登録促進オーバーレイ -->
                <div v-if="shouldShowPremiumPrompt(cat)" class="premium-prompt-overlay">
                  <div class="premium-prompt-content">
                    <!-- メッセージ -->
                    <div class="premium-prompt-message">
                      <div class="premium-badge">
                        <img src="@/assets/img/icon/premium.png" width="24" height="24" alt="Premium" />
                      </div>
                      <div class="premium-prompt-main-text">{{ $t("myPage.assistantCatDialog.premiumPrompt") }}</div>
                    </div>

                    <!-- アクションボタン -->
                    <div class="premium-prompt-actions">
                      <button class="premium-register-button" @click="onClickUpgrade">
                        {{ $t("myPage.assistantCatDialog.registerButton") }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </swiper-slide>

            <!-- ナビゲーションボタン -->
            <div class="swiper-button-prev" slot="button-prev"></div>
            <div class="swiper-button-next" slot="button-next"></div>
          </swiper>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { Swiper, SwiperSlide } from "vue-awesome-swiper";
import "swiper/css/swiper.css";
import CatIcon from "@/components/atoms/CatIcon.vue";
import LoadingSpinner from "@/components/atoms/LoadingSpinner.vue";
import type { AssistantCat, AssistantCatGroup, AssistantCatMaster } from "@/lib/models/assistantCat";
import { Plan } from "@/lib/models";
import { mapGetters } from "vuex";
import { NolaAnalytics, NolaItemId } from "@/lib/utils/analytics";

// アイコンのインポート
const laIcon = require("@/assets/img/assistant-cat/la-icon.png");
const lpIcon = require("@/assets/img/assistant-cat/lp-icon.png");
const maIcon = require("@/assets/img/assistant-cat/ma-icon.png");
const mpIcon = require("@/assets/img/assistant-cat/mp-icon.png");

// 定数定義（型安全性と再利用性の向上）
const GROUP_ORDER: AssistantCatGroup[] = ["MP", "LA", "LP", "MA"];
const TYPE_ORDER: Record<AssistantCatGroup, string[]> = {
  MP: ["EMPH", "EMPS", "IMPH", "IMPS"],
  LA: ["ELAH", "ELAS", "ILAH", "ILAS"],
  LP: ["ELPH", "ELPS", "ILPH", "ILPS"],
  MA: ["EMAH", "EMAS", "IMAH", "IMAS"],
};

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    Swiper,
    SwiperSlide,
    CatIcon,
    LoadingSpinner,
  },
  props: {
    assistantCatMaster: {
      type: Object as () => AssistantCatMaster | null,
      default: null,
    },
  },
  data() {
    return {
      assistantCats: [] as AssistantCat[],
      isLoading: false,
      selectedGroup: null as AssistantCatGroup | null,
      currentPage: 0,
      isMobile: false,
      error: null as string | null,
      nolaAnalytics: new NolaAnalytics(this.$gtm, this.$store),
    };
  },
  computed: {
    ...mapGetters("billingModule", ["hasPaymentError"]),
    swiperOptions() {
      return {
        loop: true,
        centeredSlides: true,
        slidesPerView: this.isMobile ? 1.5 : 2, // モバイル: 1.5枚、PC: 2枚
        spaceBetween: this.isMobile ? 20 : 0, // モバイル: 20px、PC: 0px
        navigation: {
          nextEl: ".swiper-button-next",
          prevEl: ".swiper-button-prev",
        },
        speed: 500,
        mousewheel: true,
      };
    },
    groupOptions() {
      return [
        { code: "MP" as AssistantCatGroup, name: this.$t("myPage.assistantCatDialog.groups.mp") as string },
        { code: "LA" as AssistantCatGroup, name: this.$t("myPage.assistantCatDialog.groups.la") as string },
        { code: "LP" as AssistantCatGroup, name: this.$t("myPage.assistantCatDialog.groups.lp") as string },
        { code: "MA" as AssistantCatGroup, name: this.$t("myPage.assistantCatDialog.groups.ma") as string },
      ];
    },
    plan() {
      return this.$store.getters["billingModule/plan"];
    },
    isFreePlan() {
      return this.plan === Plan.free;
    },
    writerTypeCat() {
      if (!this.assistantCatMaster?.writerType) {
        return null;
      }
      return (
        this.assistantCats.find((cat: AssistantCat) => cat.assistantCatType === this.assistantCatMaster.writerType) ||
        null
      );
    },
    filteredCats() {
      // データ読み込み中は空配列を返す
      if (this.assistantCats.length === 0) {
        return [];
      }

      let cats: AssistantCat[] = [];
      if (!this.selectedGroup) {
        cats = this.getOrderedCats();
      } else {
        // グループフィルタリング時もソート
        const filteredCats = this.assistantCats.filter((cat: AssistantCat) => cat.group === this.selectedGroup);
        cats = this.orderCatsByType(filteredCats);
      }

      return cats;
    },
  },
  watch: {
    selectedGroup() {
      // グループ変更時は先頭ページに移動
      this.currentPage = 0;

      // Swiperのスライド位置を先頭にリセット（loopモード対応）
      this.$nextTick(() => {
        const swiper = (this.$refs.catSwiper as { $swiper?: { slideToLoop: (index: number, speed: number) => void } })
          ?.$swiper;
        if (swiper) {
          swiper.slideToLoop(0, 0); // loopモードで0番目にアニメーションなしで移動
        }
      });
    },
  },
  methods: {
    getGroupIcon(group: AssistantCatGroup): string {
      const iconMap = {
        LA: laIcon,
        LP: lpIcon,
        MA: maIcon,
        MP: mpIcon,
      };
      return iconMap[group];
    },
    handleGroupSelect(group: AssistantCatGroup) {
      if (this.selectedGroup === group) {
        // 選択中のタブを押下するとフィルタリングを解除し、初期状態に戻す
        this.selectedGroup = null;
        this.currentPage = 0; // 初期表示位置に戻す
      } else {
        this.selectedGroup = group;
        this.currentPage = 0; // グループ選択時も先頭に移動
      }
    },
    async handleSelectCat(cat: AssistantCat) {
      try {
        this.isLoading = true;

        // APIを呼び出して選択状態を更新・Vuexストアに選択状態をキャッシュ
        await this.$store.dispatch("assistantCatModule/updateAssistantCatMaster", {
          selectedType: cat.assistantCatType,
        });

        // サーバーから最新データを取得して状態を同期
        await this.fetchAssistantCats();

        // GAイベントを発火
        this.nolaAnalytics.logButtonEvent(NolaItemId.AssistantCatChangeSelectionType, cat.assistantCatType);

        this.$notify({
          title: this.$t("myPage.assistantCatDialog.successMessage") as string,
          type: "success",
        });

        // ダイアログを閉じる
        this.handleClose();
      } catch (error) {
        console.error("Failed to change assistant cat:", error);
        this.$notify({
          title: this.$t("myPage.assistantCatDialog.errorMessage") as string,
          type: "error",
        });
      } finally {
        this.isLoading = false;
      }
    },
    handleClose() {
      this.$emit("close", true);
    },
    checkScreenSize() {
      this.isMobile = window.innerWidth <= 768;
    },
    async fetchAssistantCats() {
      this.isLoading = true;
      this.error = null;

      try {
        this.assistantCats = await this.$store.dispatch("assistantCatModule/fetchAssistantCatList");

        // データ読み込み後に選択中の猫の位置を設定
        setTimeout(() => {
          this.currentPage = 0;
        }, 100);
      } catch (error) {
        console.error("Failed to fetch assistant cats:", error);
        this.error = this.$t("myPage.assistantCatDialog.loadErrorMessage") as string;
      } finally {
        this.isLoading = false;
      }
    },
    handleRetry() {
      this.fetchAssistantCats();
    },
    shouldShowPremiumPrompt(cat: AssistantCat) {
      return this.isFreePlan && !cat.selected && !this.isWriterTypeCat(cat) && !cat.isSynergyType && !cat.isImpactType;
    },
    onClickUpgrade() {
      this.$router.push({ name: "subscriptionAnnounce" });
    },
    isWriterTypeCat(cat: AssistantCat): boolean {
      if (!this.assistantCatMaster?.writerType) {
        return false;
      }
      return cat.assistantCatType === this.assistantCatMaster.writerType;
    },
    getOrderedCats() {
      const cats = [...this.assistantCats];

      // 各タイプの猫を分類（選択中の猫を除く）
      const synergyCat = cats.find((cat) => cat.isSynergyType)!;
      const writerTypeCat = cats.find((cat) => this.isWriterTypeCat(cat))!;
      const impactCat = cats.find((cat) => cat.isImpactType)!;
      // その他の猫を取得（選択中の猫を除く）
      const otherCats = cats.filter((cat) => !cat.isSynergyType && !this.isWriterTypeCat(cat) && !cat.isImpactType)!;

      // その他の猫をグループ別に並び替え
      const orderedOtherCats = this.orderOtherCatsByGroup(otherCats);

      // 並び順を決定（選択中の猫のタイプに応じて動的に変更）
      let orderedCats: AssistantCat[] = [synergyCat, writerTypeCat, impactCat, ...orderedOtherCats];

      // 選択中の猫の位置を先頭として配列をローテーション
      const selectedCatIndex = orderedCats.findIndex((cat) => cat.selected);
      if (selectedCatIndex !== -1 && selectedCatIndex > 0) {
        const beforeSelected = orderedCats.slice(0, selectedCatIndex);
        const fromSelected = orderedCats.slice(selectedCatIndex);
        orderedCats = [...fromSelected, ...beforeSelected];
      }

      return orderedCats;
    },
    orderCatsByType(cats: AssistantCat[]) {
      // グループ内のタイプ順序でソート（TYPE_ORDERの定義通り）
      return this.orderOtherCatsByGroup(cats);
    },
    orderOtherCatsByGroup(otherCats: AssistantCat[]) {
      // Map を使用して O(1) でアクセス（パフォーマンス最適化）
      const catMap = new Map<string, AssistantCat>();
      otherCats.forEach((cat) => {
        catMap.set(cat.assistantCatType, cat);
      });

      const orderedCats: AssistantCat[] = [];

      // 定数を使用してグループ順に処理
      GROUP_ORDER.forEach((group) => {
        const groupTypeOrder = TYPE_ORDER[group];

        // グループ内のタイプ順に並び替え
        groupTypeOrder.forEach((type) => {
          const cat = catMap.get(type);
          if (cat) {
            orderedCats.push(cat);
          }
        });
      });

      return orderedCats;
    },
    /**
     * vue-awesome-swiperの内部動作で初期表示→左に移動した場合に変更するボタンが効かない問題を解消
     *
     * 【原因】
     * Swiperのloop modeでは、スライドのDOM要素が複製される
     * - 実スライド: Vueがレンダリングした元のDOM（@clickイベントハンドラ付き）
     * - duplicateスライド: Swiperが自動生成した複製DOM（イベントハンドラがコピーされない）
     *
     * 左に移動すると配列の最後のスライドの複製（duplicate）が表示されますが、この複製には
     * Vueの@click="handleSelectCat(cat)"が存在しないため、ボタンをクリックしても何も起こらない
     *
     * 【解決方法】
     * イベント委譲パターンを使用
     * 1. Swiperコンテナ全体でクリックイベントをキャッチ
     * 2. クリックされた要素がボタンかチェック
     * 3. スライドのdata-swiper-slide-index属性から実際のデータインデックスを取得
     * 4. filteredCats[index]で対応する猫データを取得して処理を実行
     */
    handleSwiperClick(event: MouseEvent) {
      // クリックされた要素がボタンかどうかをチェック
      const target = event.target as HTMLElement;
      const button = target.closest(".action-button--active, .premium-register-button");

      if (!button) {
        return; // ボタン以外のクリックは無視
      }

      // クリックされたスライドを取得
      const slide = target.closest(".swiper-slide") as HTMLElement;
      if (!slide) {
        return;
      }

      // data-swiper-slide-index属性から実際のインデックスを取得
      const slideIndex = slide.getAttribute("data-swiper-slide-index");
      if (slideIndex === null) {
        return;
      }

      // インデックスに対応するcatデータを取得
      const cat = this.filteredCats[parseInt(slideIndex, 10)];
      if (!cat) {
        return;
      }

      // ボタンの種類に応じて処理を実行
      if (button.classList.contains("premium-register-button")) {
        this.onClickUpgrade();
      } else if (button.classList.contains("action-button--active")) {
        // プレミアムプロンプトが表示されている場合は変更ボタンのクリックを無視
        if (this.shouldShowPremiumPrompt(cat)) {
          return;
        }
        this.handleSelectCat(cat);
      }
    },
  },
  async mounted() {
    // bodyのスクロールを無効化
    document.body.style.overflow = "hidden";

    // 画面サイズの初期チェック
    this.checkScreenSize();
    window.addEventListener("resize", this.checkScreenSize);

    // アシスタント猫データを取得
    await this.fetchAssistantCats();

    // Swiperのduplicateスライド内のボタンクリックを処理するためのイベント委譲
    this.$nextTick(() => {
      const swiperContainer = this.$refs.catSwiper?.$el;
      if (swiperContainer) {
        swiperContainer.addEventListener("click", this.handleSwiperClick);
      }
    });
  },
  beforeDestroy() {
    // bodyのスクロールを元に戻す
    document.body.style.overflow = "";

    // Swiperコンテナのイベントリスナーを削除
    const swiperContainer = this.$refs.catSwiper?.$el;
    if (swiperContainer) {
      swiperContainer.removeEventListener("click", this.handleSwiperClick);
    }

    // イベントリスナーを削除
    window.removeEventListener("resize", this.checkScreenSize);
  },
});

interface Props {
  assistantCatMaster: AssistantCatMaster | null;
}

interface Data {
  assistantCats: AssistantCat[];
  isLoading: boolean;
  selectedGroup: AssistantCatGroup | null;
  currentPage: number;
  isMobile: boolean;
  error: string | null;
  nolaAnalytics: NolaAnalytics;
}

interface Computed {
  groupOptions: Array<{ code: AssistantCatGroup; name: string }>;
  plan: Plan;
  isFreePlan: boolean;
  writerTypeCat: AssistantCat | null;
  filteredCats: AssistantCat[];
  hasPaymentError: boolean;
  swiperOptions: {
    loop: boolean;
    centeredSlides: boolean;
    slidesPerView: number;
    spaceBetween: number;
    navigation: {
      nextEl: string;
      prevEl: string;
    };
    speed: number;
    mousewheel: boolean;
  };
}

interface Methods {
  getGroupIcon: (group: AssistantCatGroup) => string;
  handleGroupSelect: (group: AssistantCatGroup) => void;
  handleSelectCat: (cat: AssistantCat) => Promise<void>;
  handleClose: () => void;
  checkScreenSize: () => void;
  fetchAssistantCats: () => Promise<void>;
  handleRetry: () => void;
  shouldShowPremiumPrompt: (cat: AssistantCat) => boolean;
  onClickUpgrade: () => void;
  isWriterTypeCat: (cat: AssistantCat) => boolean;
  getOrderedCats: () => AssistantCat[];
  orderCatsByType: (cats: AssistantCat[]) => AssistantCat[];
  orderOtherCatsByGroup: (otherCats: AssistantCat[]) => AssistantCat[];
  handleSwiperClick: (event: MouseEvent) => void;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";
.assistant-cat-dialog-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-overlay);
  z-index: 9000;
  padding: 20px;
}

.assistant-cat-dialog-content {
  background: var(--color-background-normal);
  border-radius: var(--radius-md);
  max-width: 600px;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  position: relative;
  box-shadow: var(--shadow-lg);
  color: var(--color-text-normal);
}

.dialog-header {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 24px 0px 24px;
  position: relative;
}

.dialog-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  margin: 0;
  color: var(--color-text-normal);
  text-align: center;
}

.close-button {
  background: none;
  border: none;
  font-size: var(--font-size-xl);
  cursor: pointer;
  color: var(--color-text-low);
  padding: 4px;
  border-radius: var(--radius-xs);
  transition: background-color 0.2s;
  position: absolute;
  top: 20px;
  right: 24px;
  z-index: 10000;

  @include hover-overlay;
}

.close-icon {
  font-weight: var(--font-weight-bold);
  line-height: 1;
}

.loading-container {
  position: relative;
  height: 400px;
}

.error-container {
  padding: 40px 24px;
  text-align: center;
  height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.error-message {
  font-size: var(--font-size-md);
  color: var(--color-warning);
  margin: 0 0 24px 0;
  line-height: 1.6;
}

.retry-button {
  position: relative;
  padding: 12px 24px;
  background: var(--color-primary);
  color: var(--color-text-on-fixed-bg);
  border: none;
  border-radius: var(--radius-lg);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  transition: all 0.2s;

  @include hover-overlay;

  &:hover {
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
}

.dialog-body {
  padding: 20px 0 32px 0;
}

.description {
  font-size: var(--font-size-sm);
  color: var(--color-text-low);
  padding: 0 24px;
  margin: 0 0 20px 0;
  text-align: center;
  line-height: 1.6;
}

.group-tabs {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin: 0 0 24px 0;
  padding: 0 24px;
}

.group-tab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 2px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-background-normal);
  cursor: pointer;
  transition: all 0.2s;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-low);
  min-width: 100px;
  justify-content: center;
  line-height: 1;

  span {
    transform: translateY(1px);
  }

  &:hover {
    border-color: var(--color-text-low);
    transform: translateY(-1px);
  }

  &--active {
    &.group-tab--la {
      border-color: #51cf66;
      background: rgba(81, 207, 102, 0.1);
      box-shadow: 0 2px 8px rgba(81, 207, 102, 0.2);
    }

    &.group-tab--lp {
      border-color: #4dabf7;
      background: rgba(77, 171, 247, 0.1);
      box-shadow: 0 2px 8px rgba(77, 171, 247, 0.2);
    }

    &.group-tab--ma {
      border-color: #845ef7;
      background: rgba(132, 94, 247, 0.1);
      box-shadow: 0 2px 8px rgba(132, 94, 247, 0.2);
    }

    &.group-tab--mp {
      border-color: #ff6b6b;
      background: rgba(255, 107, 107, 0.1);
      box-shadow: 0 2px 8px rgba(255, 107, 107, 0.2);
    }
  }
}

.group-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}

.carousel-container {
  height: 450px;
  background: var(--color-background-normal);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;

  .cat-swiper {
    height: 100%;
    width: 100%;
  }

  :deep(.swiper-button-prev),
  :deep(.swiper-button-next) {
    background: rgba(0, 0, 0, 0.6);
    color: var(--color-text-on-fixed-bg);
    border-radius: var(--radius-rounded);
    width: 40px;
    height: 40px;
    transition: all 0.2s;
    box-shadow: var(--shadow-sm);

    &:after {
      font-size: var(--font-size-md);
      font-weight: var(--font-weight-bold);
    }

    &:hover {
      background: rgba(0, 0, 0, 0.8);
    }

    &.swiper-button-disabled {
      background: rgba(0, 0, 0, 0.3);
      color: rgba(255, 255, 255, 0.5);
      cursor: not-allowed;
    }
  }

  :deep(.swiper-button-prev) {
    left: 10px;
  }

  :deep(.swiper-button-next) {
    right: 10px;
  }
}

.cat-slide {
  padding: 20px 0px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;

  // デフォルト: すべてのボタンを無効化
  .action-button,
  .premium-register-button {
    pointer-events: none;
  }
}

// Swiperのアクティブスライドにスタイルを適用（:deep()でSwiperの内部クラスにアクセス）
:deep(.swiper-slide.cat-slide) {
  // ダミースライドは基本的に無効（非アクティブ時のみ）
  &.swiper-slide-duplicate:not(.swiper-slide-active) {
    .action-button,
    .premium-register-button {
      pointer-events: none !important;
    }
  }

  // アクティブなスライドはduplicateであってもボタンを有効化し、上部にシフト
  &.swiper-slide-active {
    transform: translateY(-20px);

    .action-button,
    .premium-register-button {
      pointer-events: auto !important;
      cursor: pointer;
    }
  }
}

.cat-card {
  background: var(--color-background-normal);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 16px;
  text-align: center;
  position: relative;
  display: flex;
  flex-direction: column;
  transition: all 0.2s;
  box-shadow: var(--shadow-sm);
  min-height: 350px;
  max-width: 230px;
  width: 230px;

  &:hover {
    box-shadow: var(--shadow-md);
  }

  // プレミアム登録促進カードのスタイル
  &--premium-prompt {
    .cat-action {
      pointer-events: none;
    }
  }
}

.cat-group-badge {
  position: absolute;
  top: 10px;
  right: 10px;
}

.cat-group-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.cat-icon-container {
  margin: 8px 0 12px 0;
  display: flex;
  justify-content: center;
}

.cat-names {
  margin-bottom: 8px;
}

.cat-name-japanese {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
  margin-bottom: 4px;
}

.cat-name-english {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-low);
}

.cat-status {
  min-height: 16px;
  margin-bottom: 8px;
}

.status-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  display: inline-block;

  &--writer-type,
  &--synergy,
  &--impact {
    &.status-label--la {
      color: #51cf66; // ひらめき：緑
    }

    &.status-label--lp {
      color: #4dabf7; // 知性：青
    }

    &.status-label--ma {
      color: #845ef7; // 感性：紫
    }

    &.status-label--mp {
      color: #ff6b6b; // 追求心：赤
    }
  }
}

.cat-description {
  font-size: var(--font-size-xs);
  color: var(--color-text-low);
  line-height: 1.5;
  margin: 0 0 12px 0;
  text-align: left;
}

.cat-action {
  margin-top: auto;
}

.action-button {
  position: relative;
  width: 100%;
  padding: 10px 16px;
  border-radius: var(--radius-lg);
  border: none;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  transition: all 0.2s;

  &--active {
    background: var(--color-primary);
    color: var(--color-text-on-fixed-bg);

    @include hover-overlay;
  }

  &--disabled {
    background: var(--color-inactive);
    color: var(--color-text-on-fixed-bg);
    cursor: not-allowed;
    border: 1px solid var(--color-inactive);
  }
}

// プレミアム登録促進オーバーレイ
.premium-prompt-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--color-overlay);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto; // オーバーレイ全体でクリックを遮断
  z-index: 10;
}

.premium-prompt-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--color-text-on-fixed-bg);
  padding: 20px;
  pointer-events: auto;
}

.premium-badge {
  flex-shrink: 0;

  img {
    width: 24px;
    height: 24px;
  }
}

.premium-prompt-message {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;

  .premium-prompt-main-text {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-bold);
    line-height: 1.4;
    text-align: left;
  }
}

.premium-prompt-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.premium-register-button {
  position: relative;
  width: 100%;
  padding: 10px 16px;
  border-radius: var(--radius-lg);
  border: none;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  transition: all 0.2s;
  background: var(--color-brand-premium);
  color: var(--color-text-on-fixed-bg);

  @include hover-overlay;

  &:hover {
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
}

@media (max-width: 1024px) {
  .assistant-cat-dialog-content {
    max-width: 600px;
    margin: 20px;
    max-height: calc(100vh - 40px);
  }
}

@media (max-width: 768px) {
  .assistant-cat-dialog-content {
    max-width: 600px;
    margin: 10px;
    max-height: calc(100vh - 20px);
  }

  .dialog-header {
    padding: 15px 20px 0px 20px;
  }

  .close-button {
    top: 15px;
    right: 20px;
    font-size: var(--font-size-lg);
  }

  .group-tabs {
    gap: 4px;
    margin: 0 0 24px 0;
    padding: 0 20px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */

    &::-webkit-scrollbar {
      display: none; /* Chrome, Safari, Opera */
    }
  }

  .group-tab {
    padding: 6px 8px;
    font-size: var(--font-size-xs);
    min-width: 60px;
  }

  .carousel-container {
    height: 450px;
    margin: 0;
    padding: 0;

    :deep(.swiper-button-prev) {
      left: 5px;
      width: 32px;
      height: 32px;

      &:after {
        font-size: var(--font-size-sm);
      }
    }

    :deep(.swiper-button-next) {
      right: 5px;
      width: 32px;
      height: 32px;

      &:after {
        font-size: var(--font-size-sm);
      }
    }
  }

  .cat-card {
    min-height: 350px;
    max-width: 240px;
    width: 240px;
  }
}

@media (max-width: 576px) {
  .assistant-cat-dialog-content {
    margin: 5px;
    max-height: calc(100vh - 10px);
  }

  .dialog-header {
    padding: 10px 15px 0px 15px;
  }

  .close-button {
    top: 10px;
    right: 15px;
    font-size: var(--font-size-md);
  }

  .group-tabs {
    flex-wrap: nowrap;
    gap: 3px;
    margin: 0 0 24px 0;
    padding: 0 15px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */

    &::-webkit-scrollbar {
      display: none; /* Chrome, Safari, Opera */
    }
  }

  .group-tab {
    padding: 5px 8px;
    font-size: var(--font-size-xxs);
    min-width: 55px;
    flex-shrink: 0;
  }

  .group-icon {
    width: 14px;
    height: 14px;
  }

  .carousel-container {
    height: 450px;
    margin: 0;
    padding: 0;

    :deep(.swiper-button-prev) {
      left: 3px;
      width: 28px;
      height: 28px;

      &:after {
        font-size: var(--font-size-xs);
      }
    }

    :deep(.swiper-button-next) {
      right: 3px;
      width: 28px;
      height: 28px;

      &:after {
        font-size: var(--font-size-xs);
      }
    }
  }

  .cat-card {
    min-height: 320px;
    max-width: 200px;
    width: 200px;
    padding: 12px;
  }
}
</style>
